import { FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import FlagIcon from "../components/FlagIcon";
import ValidationTextField from "../components/ValidationTextField";
import { useLanguage } from "../hooks/useLanguage";
import { getUnitLabel, UNIT_CHOICES } from "../utils/lib";
import AttributeRegionSelection from "./AttributeRegionSelection";

const AttributeRegionValues = forwardRef(({ userInfo, updateDefaults, updateValidation, defaults, validation, attributeType, unitFamily, region }, ref) => {
  const { locale } = useLanguage();

  const inputRefs = useRef({});

  const intl = useIntl();

  const textFieldStyle = {
    width: '90%'
  };

  const getValue = (v) => {
    return (v == null || v === '') ? '' : v;
  }

  /**
   * Validates all current input-fields.
   * @returns true if all input-fields are valid
   */
  const validateAllInput = useCallback(() => {
    if (inputRefs.current) {
      for (let key in inputRefs.current) {
        if (!inputRefs.current[key].checkValidity()) {
          return false;
        }
      }
    }
    return true;
  }, []);

  /**
   * Returns false, if the unitFamily is set, a value is set, but no unit is given.
   * Otherwise returns true.
   */
  const checkUnitValueConstraint = useCallback((unitFamily, defaults, validation) => {
    return unitFamily == null || getValue(defaults.value) === '' || validation.unit != null;
  }, [getValue]);

  /**
   * Expose validateAllInput-method via ref.
   */
  useImperativeHandle(ref, () => ({
    validateAllInput: validateAllInput,
    checkUnitValueConstraint: checkUnitValueConstraint
  }), [validateAllInput, checkUnitValueConstraint]);

  /**
   * The units to choose from.
   */
  const unitSelectionValues = useMemo(() => {
    return UNIT_CHOICES[unitFamily] || [];
  }, [unitFamily]);

  const gridElements = useMemo(() => {
    inputRefs.current = {};
    switch (attributeType) {
      case 'TEXT':
        const textInputProps = {};
        const minInputProps = { step: 1, min: 0 };
        const maxInputProps = { step: 1, min: 0 };
        if (validation.minLength != null) {
          textInputProps.minLength = validation.minLength;
          maxInputProps.min = validation.minLength;
        }
        if (validation.maxLength != null) {
          textInputProps.maxLength = validation.maxLength;
          minInputProps.max = validation.maxLength;
        }
        if (validation.pattern != null && validation.pattern) {
          textInputProps.pattern = validation.pattern;
        }
        return [
          <Grid item xs={12} sm={6} md={4} key="g1" >
            <ValidationTextField
              required={false}
              sx={textFieldStyle}
              id="text-value"
              label={intl.formatMessage({ id: "EditAttribute.defaultValue" })}
              variant="outlined"
              type="text"
              value={defaults.value || ''}
              onChange={event => updateDefaults({ value: event.target.value })}
              inputProps={textInputProps}
              ref={(el) => inputRefs.current['text-value'] = el}
            >
            </ValidationTextField>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g2" >
            <ValidationTextField
              required={false}
              sx={textFieldStyle}
              id="text-min-length"
              label={intl.formatMessage({ id: "EditAttribute.minLength" })}
              variant="outlined"
              type="number"
              inputProps={minInputProps}
              value={getValue(validation.minLength)}
              onChange={event => updateValidation({ ...validation, minLength: event.target.value })}
              ref={(el) => inputRefs.current['text-min-length'] = el}
            >
            </ValidationTextField>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g3" >
            <ValidationTextField
              required={false}
              sx={textFieldStyle}
              id="text-max-length"
              label={intl.formatMessage({ id: "EditAttribute.maxLength" })}
              variant="outlined"
              type="number"
              inputProps={maxInputProps}
              value={getValue(validation.maxLength)}
              onChange={event => updateValidation({ ...validation, maxLength: event.target.value })}
              ref={(el) => inputRefs.current['text-max-length'] = el}
            >
            </ValidationTextField>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g4" >
            <TextField
              required={false}
              sx={textFieldStyle}
              id="text-pattern"
              label={intl.formatMessage({ id: "EditAttribute.pattern" })}
              variant="outlined"
              type="text"
              value={validation.pattern || ''}
              onChange={event => updateValidation({ ...validation, pattern: event.target.value })}
            >
            </TextField>
          </Grid>
        ]
      case 'SELECTION':
        return <AttributeRegionSelection updateDefaults={updateDefaults} updateValidation={updateValidation} validation={validation} defaults={defaults} />
      case 'NUMBER':
        const valueNumberInputProps = {};
        const minNumberInputProps = {};
        const maxNumberInputProps = {};
        const muiInputProps = {};
        if (validation.min != null) {
          valueNumberInputProps.min = validation.min;
          maxNumberInputProps.min = validation.min;
        }
        if (validation.max != null) {
          valueNumberInputProps.max = validation.max;
          minNumberInputProps.max = validation.max;
        }
        if (validation.step != null) {
          valueNumberInputProps.step = validation.step;
        }
        if (unitFamily != null && validation.unit) {
          muiInputProps.endAdornment = <InputAdornment position="end">{getUnitLabel(validation.unit, unitFamily)}</InputAdornment>
        }
        return [
          <Grid item xs={12} sm={6} md={4} key="g1" >
            <ValidationTextField
              required={false}
              sx={textFieldStyle}
              id="number-default-value"
              label={intl.formatMessage({ id: "EditAttribute.defaultValue" })}
              variant="outlined"
              type="number"
              inputProps={valueNumberInputProps}
              InputProps={muiInputProps}
              value={getValue(defaults.value)}
              onChange={event => updateDefaults({ ...defaults, value: event.target.value })}
              ref={(el) => inputRefs.current['number-default-value'] = el}
            >
            </ValidationTextField>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g2" sx={{ display: unitFamily != null ? 'flex' : 'none' }} >
            <FormControl variant="outlined" sx={textFieldStyle} >
              <InputLabel id="selected-number-unit-label" ><FormattedMessage id="EditAttribute.unit" /></InputLabel>
              <Select
                required={getValue(defaults.value) !== '' && unitFamily != null}
                labelId="selected-number-unit-label"
                value={validation.unit || ''}
                onChange={event => updateValidation({ ...validation, unit: event.target.value || null })}
                label={<FormattedMessage id="EditAttribute.unit" />}
              >
                <MenuItem value={''}><FormattedMessage id="EditAttribute.none" /></MenuItem>
                {
                  unitSelectionValues.map(({ value, label }, index) => <MenuItem key={index} value={value} >{label}</MenuItem>)
                }
              </Select>
            </FormControl>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g3" >
            <ValidationTextField
              required={false}
              sx={textFieldStyle}
              id="number-min-value"
              label={intl.formatMessage({ id: "EditAttribute.minValue" })}
              variant="outlined"
              type="number"
              inputProps={minNumberInputProps}
              value={getValue(validation.min)}
              onChange={event => updateValidation({ ...validation, min: event.target.value })}
              ref={(el) => inputRefs.current['number-min-value'] = el}
            >
            </ValidationTextField>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g4" >
            <ValidationTextField
              required={false}
              sx={textFieldStyle}
              id="number-max-value"
              label={intl.formatMessage({ id: "EditAttribute.maxValue" })}
              variant="outlined"
              type="number"
              inputProps={maxNumberInputProps}
              value={getValue(validation.max)}
              onChange={event => updateValidation({ ...validation, max: event.target.value })}
              ref={(el) => inputRefs.current['number-max-value'] = el}
            >
            </ValidationTextField>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g5" >
            <TextField
              required={false}
              sx={textFieldStyle}
              id="number-step-value"
              label={intl.formatMessage({ id: "EditAttribute.step" })}
              variant="outlined"
              type="number"
              inputProps={{}}
              value={getValue(validation.step)}
              onChange={event => updateValidation({ ...validation, step: event.target.value })}
            >
            </TextField>
          </Grid>,
          <Grid item xs={12} sm={6} md={4} key="g6" >
            <ValidationTextField
              required={false}
              sx={textFieldStyle}
              id="number-precision-value"
              label={intl.formatMessage({ id: "EditAttribute.precision" })}
              variant="outlined"
              type="number"
              inputProps={{ step: 1, min: 0 }}
              value={getValue(validation.precision)}
              onChange={event => updateValidation({ ...validation, precision: event.target.value })}
              ref={(el) => inputRefs.current['number-precision'] = el}
            >
            </ValidationTextField>
          </Grid>,
        ];
      case 'BOOL':
        return (
          <Grid item xs={12} sm={6} md={3} >
            <TextField
              id="attribute-value"
              select
              sx={textFieldStyle}
              label={intl.formatMessage({ id: "EditAttribute.defaultValue" })}
              variant="standard"
              value={defaults.value ?? ''}
              onChange={event => updateDefaults({ value: event.target.value !== '' ? event.target.value : null })}
            >
              <MenuItem value={''}><FormattedMessage id="EditAttribute.noDefaultValue" /></MenuItem>
              <MenuItem key={false} value={false} ><FormattedMessage id="AttributeBox.false" /></MenuItem>
              <MenuItem key={true} value={true} ><FormattedMessage id="AttributeBox.true" /></MenuItem>
            </TextField>
          </Grid>
        );
      default:
        return [];
    }
  }, [attributeType, defaults, validation, locale, intl, updateDefaults, updateValidation, unitFamily, unitSelectionValues]);

  return (
    <Grid container spacing={3} sx={{ alignItems: 'flex-start', margin: 0, width: "100%", paddingBottom: 2 }} >
      <Grid item xs={12} >
        <FlagIcon code={region === 'GLOBAL' ? 'UN' : region} />
      </Grid>
      {gridElements}
    </Grid>
  );
});

export default AttributeRegionValues;